<!-- PDVideos Copyright 2022 Rebecca L Beaudet -->
<script lang="ts">
  import { CORP_NAME } from '../language/constantContent';
  import {
    pdVideoPitch,
    pdVideos,
    customerMsg,
    videoReqLag,
  } from '../language/videoContent';
  import { upsertProfile, profile } from '../Persistence/profileDb';
  import { stripe, stripeLoading } from '../Stores/stripeStore';
  import {
    BACKEND_URL,
    STRIPE_PUBLISHABLE_KEY,
  } from '../language/connectionConst';
  import ErrorComponent from './pageComponents/ErrorComponent.svelte';
  import { errorMsg } from '../language/errors';

  let showCheckout: boolean = false;
  let totalAmount: number = 0;
  let totalVideos: number = 0;
  let needStripe: boolean = false;
  let elements;
  let paymentElement;
  let name: string = $profile.name;
  let email: string = $profile.email;
  let paymentError: string = '';
  let selectedVideos: number[] = [];
  let validPaymentInfo: boolean = false;
  let readyToBuy: boolean = false;

  profile.subscribe((user) => {
    name = user.name;
    email = user.email;
  });

  $: validMail =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      String(email).toLowerCase(),
    );

  $: validName = name ? true : false;

  const loadStripe = () => {
    if (!$stripe) $stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
  };

  // returns onclick event for each product
  const addToCart = (videoId: number, selected: boolean) => {
    return () => {
      let anyItemSelected = false;
      let videosSelected = 0;
      let grossAmount = 0;
      // show that cart item is selected
      pdVideos.update((videos) => {
        return videos.map((video) => {
          if (video.id === videoId) {
            if (!selected) {
              anyItemSelected = true;
              grossAmount += video.price;
              videosSelected++;
            }
            return {
              ...video,
              selected: !selected,
            };
          }
          if (video.selected) {
            anyItemSelected = true;
            videosSelected++;
            grossAmount += video.price;
          }
          return video;
        });
      });
      selectedVideos = $pdVideos
        .filter((video) => video.selected)
        .map((video) => video.id);
      showCheckout = anyItemSelected;
      totalVideos = videosSelected;
      totalAmount = grossAmount;
    };
  };

  // Collects email and name. Confirms the quantity and amount of items
  // Sends password to retrieve purchases in future
  const checkout = async () => {
    needStripe = true;
    $stripeLoading = true;
    try {
      // creates or updates profile
      const { password } = await upsertProfile({ email, name });
      const data = {
        name,
        email,
        selectedVideos,
        password,
      };
      const res = await fetch(BACKEND_URL + 'prod/pdBuyingIntent', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!res) {
        paymentError = errorMsg.noResponseFromUs;
        $stripeLoading = false;
        return;
      }
      const { clientSecret } = await res.json();
      if (!clientSecret) {
        paymentError = errorMsg.noClientSecretForStripe;
        $stripeLoading = false;
        return;
      }
      const waitForStripe = $stripe ? 0 : 1000;
      const setUpCardElement = () => {
        setTimeout(() => {
          if (!$stripe) {
            setUpCardElement();
            return;
          }
          elements = $stripe.elements({ clientSecret });
          paymentElement = elements.create('payment');
          paymentElement.on('change', ({ error, complete, collapsed }) => {
            if (!readyToBuy && !collapsed) {
              readyToBuy = true;
              $stripeLoading = false;
            }
            validPaymentInfo = !error && complete;
            paymentError = error ? `Payment processor: ${error.message}` : '';
          });
          paymentElement.mount('#payment-element');
        }, waitForStripe);
      };
      setUpCardElement();
    } catch (error) {
      paymentError = errorMsg.badRobots;
      console.error(error);
    }
  };

  const buy = async () => {
    $stripeLoading = true;
    await upsertProfile({ lastPurchaseTime: Date.now() });
    const result = await $stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
    });
    if (result.error) {
      await upsertProfile({ lastPurchaseTime: 0 });
      paymentError = `Payment processor: ${result.error.message}`;
      $stripeLoading = false;
      return;
    }
    // The page will refresh if a payment is successful, this timeout should fail in that way
    setTimeout(async () => {
      await upsertProfile({ lastPurchaseTime: 0 });
      paymentError = errorMsg.failedPayment;
      $stripeLoading = false;
    }, 10000);
  };
</script>

<svelte:head>
  {#if !$stripe && needStripe}
    <script src="https://js.stripe.com/v3/" on:load={loadStripe}></script>
  {/if}
</svelte:head>

<div class="container mt-3">
  <div class="row" style="">
    <!-- checkout sidebar -->
    <div class={showCheckout ? 'col-12' : 'd-none'}>
      <ErrorComponent errorMsg={paymentError} />
      <div id="payment-element" />
      {#if readyToBuy}
        <button
          type="button"
          id="buy_videos"
          class="btn btn-lg btn-primary mt-3 mb-3"
          disabled={$stripeLoading || !validPaymentInfo}
          on:click={buy}
        >
          {`Buy ${totalVideos} videos for $${(totalAmount / 100).toFixed(2)}`}
        </button>
      {:else}
        <div class="card floating-card">
          <div class="card-header fs-4">Purchase Cart</div>
          <ErrorComponent errorMsg={paymentError} />
          {#if $profile.name && $profile.email}
            <ul class="list-group list-group-flush">
              <li class="list-group-item fs-5">Name: {$profile.name}</li>
              <li class="list-group-item fs-5">Email: {$profile.email}</li>
            </ul>
          {:else}
            <div class="m-1">
              <label for="name" class="form-label">Name</label>
              <input
                type="text"
                class="form-control"
                id="name"
                placeholder="Name"
                bind:value={name}
                aria-label="Name"
              />
            </div>
            <div class="m-1">
              <label for="email" class="form-label">Email</label>
              <input
                type="text"
                class="form-control"
                id="email"
                placeholder="Email"
                bind:value={email}
                aria-label="Email"
              />
            </div>
          {/if}
          <button
            type="button"
            id="checkout_videos"
            class="btn btn-lg btn-primary m-3"
            title="checkout"
            disabled={!validMail || !validName}
            on:click={checkout}
          >
            {`Checkout: ${totalVideos} video${
              totalVideos > 1 ? 's' : ''
            } for $${(totalAmount / 100).toFixed(2)}`}
          </button>
        </div>
      {/if}
    </div>
    <!-- learning portal -->
    <div class={readyToBuy ? 'd-none' : 'col-12'}>
      <div style="height: 100%; overflow-y: auto;">
        <h3>Learn with {CORP_NAME}</h3>
        <p>{pdVideoPitch}</p>
        {#if $profile.name}
          <p>{customerMsg($profile.name, $profile.email)}</p>
        {/if}

        <div class="container" style="height: max-content;">
          <div class="row">
            <h4>Current Catalog</h4>
            {#each $pdVideos as video}
              <div class="col-auto p-4">
                <div
                  class={`card${
                    video.selected
                      ? ' border-warning text-white bg-secondary'
                      : ''
                  }`}
                  style="width: 18rem;"
                >
                  {#if video.link}
                    <iframe
                      title={video.title}
                      id={`youtube_${video.id}`}
                      class="card-img-top"
                      src={`https://youtube.com/embed/${video.link}`}
                      allow="autoplay; encrypted-media"
                      allowfullscreen={true}
                      width="560"
                      height="315"
                      frameborder="0"
                    />
                  {:else if $videoReqLag}
                    <div class="d-flex justify-content-center my-5">
                      <div
                        class="spinner-border"
                        style="width: 4rem; height: 4rem;"
                        role="status"
                      >
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  {:else}
                    <button
                      type="button"
                      id={video.selected
                        ? `remove_cart_${video.id}`
                        : `add_cart_${video.id}`}
                      class={`btn btn-lg ${
                        video.selected ? 'btn-danger' : 'btn-success'
                      }`}
                      on:click={addToCart(video.id, video.selected)}
                    >
                      {`${video.selected ? 'Remove from' : 'Add to'} Cart: ${
                        video.selected ? '-' : ''
                      }$${(video.price / 100).toFixed(2)}`}
                    </button>
                    <img
                      src={video.thumbnail}
                      class="card-img-top"
                      alt={video.thumbnailDesc}
                    />
                  {/if}
                  <div class="card-body">
                    <h5 class="card-title">{video.title}</h5>
                    <p class="card-text">{video.description}</p>
                  </div>
                </div>
              </div>
            {:else}
              <p>Loading videos...</p>
            {/each}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .floating-card {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    z-index: 1030;
  }
</style>
